import React from 'react';
type DecorProps = {
  className?: string;
};
type FlowerProps = DecorProps & {
  /** Text-color class for the petals, e.g. "text-rose-300". */
  petalClassName?: string;
  /** Text-color class for the flower center. */
  centerClassName?: string;
};

/** Botanical sprig with leaves — decorative. */
export function Sprig({
  className = ''
}: DecorProps) {
  return <svg viewBox="0 0 120 200" fill="none" aria-hidden="true" className={className} data-id="element-115" data-mp="3jdzvxa6fhyku">
      <path d="M60 196V16" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" data-id="element-116" data-mp="ayg8irsfvadp" />
      {[0, 1, 2, 3, 4].map(index => {
      const y = 44 + index * 30;
      return <g key={index} data-id="element-117" data-mp="15qy808mk2jy">
            <path d={`M60 ${y} C40 ${y - 16} 20 ${y - 10} 12 ${y + 6} C30 ${y + 18} 50 ${y + 12} 60 ${y}Z`} fill="currentColor" opacity="0.35" data-id="element-118" data-mp="16a7yto4fkmw" />
            <path d={`M60 ${y + 14} C80 ${y - 2} 100 ${y + 4} 108 ${y + 20} C90 ${y + 32} 70 ${y + 26} 60 ${y + 14}Z`} fill="currentColor" opacity="0.22" data-id="element-119" data-mp="tuo391nts2ur" />
          </g>;
    })}
      <circle cx="60" cy="16" r="7" fill="currentColor" opacity="0.5" data-id="element-120" data-mp="3f8sad261r7jn" />
    </svg>;
}

/** Five-petal blossom (wild rose) — decorative. */
export function Blossom({
  className = '',
  petalClassName = '',
  centerClassName = ''
}: FlowerProps) {
  return <svg viewBox="0 0 100 100" fill="none" aria-hidden="true" className={className} data-id="element-121" data-mp="3ozdiphbivqfr">
      <g className={petalClassName} data-id="element-122" data-mp="3k7n6f5t0yslp">
        {[0, 1, 2, 3, 4].map(index => <ellipse key={index} cx="50" cy="26" rx="14" ry="22" fill="currentColor" opacity="0.55" transform={`rotate(${index * 72} 50 50)`} data-id="element-123" data-mp="2rdk3auh9xwm" />)}
      </g>
      <circle cx="50" cy="50" r="9" fill="currentColor" opacity="0.85" className={centerClassName} data-id="element-124" data-mp="3twwy4kjyi99a" />
    </svg>;
}

/** Many-petal daisy / chamomile — decorative. */
export function Daisy({
  className = '',
  petalClassName = '',
  centerClassName = ''
}: FlowerProps) {
  return <svg viewBox="0 0 100 100" fill="none" aria-hidden="true" className={className} data-id="element-125" data-mp="2din8ln3zaegv">
      <g className={petalClassName} data-id="element-126" data-mp="28kvf4onoqkak">
        {Array.from({
        length: 14
      }, (_, index) => <ellipse key={index} cx="50" cy="22" rx="5.5" ry="20" fill="currentColor" opacity="0.7" transform={`rotate(${index * 360 / 14} 50 50)`} data-id="element-127" data-mp="chgyhnncu0vw" />)}
      </g>
      <circle cx="50" cy="50" r="12" fill="currentColor" className={centerClassName} data-id="element-128" data-mp="hgk3j83c473v" />
    </svg>;
}

/** Bell-shaped flowers on an arching stem (gentian / bluebell) — decorative. */
export function Bellflower({
  className = '',
  petalClassName = '',
  centerClassName = ''
}: FlowerProps) {
  const bells = [{
    x: 34,
    y: 60,
    r: -18
  }, {
    x: 58,
    y: 92,
    r: 8
  }, {
    x: 40,
    y: 124,
    r: -10
  }];
  return <svg viewBox="0 0 100 200" fill="none" aria-hidden="true" className={className} data-id="element-129" data-mp="1564otxmynggb">
      <g className={centerClassName} data-id="element-130" data-mp="1588scegy6dg2">
        <path d="M70 196C70 140 64 80 44 24" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" opacity="0.6" data-id="element-131" data-mp="1a58y0o9vxe6n" />
        <path d="M68 150C54 146 44 150 36 162C50 166 60 162 68 150Z" fill="currentColor" opacity="0.4" data-id="element-132" data-mp="1e85lasaddygx" />
      </g>
      <g className={petalClassName} data-id="element-133" data-mp="1j5xavfnjl8hy">
        {bells.map(bell => <path key={bell.y} transform={`rotate(${bell.r} ${bell.x} ${bell.y})`} d={`M${bell.x - 12} ${bell.y - 16}C${bell.x - 14} ${bell.y + 2} ${bell.x - 18} ${bell.y + 12} ${bell.x - 20} ${bell.y + 18}L${bell.x - 10} ${bell.y + 13}L${bell.x} ${bell.y + 19}L${bell.x + 10} ${bell.y + 13}L${bell.x + 20} ${bell.y + 18}C${bell.x + 18} ${bell.y + 12} ${bell.x + 14} ${bell.y + 2} ${bell.x + 12} ${bell.y - 16}C${bell.x + 6} ${bell.y - 22} ${bell.x - 6} ${bell.y - 22} ${bell.x - 12} ${bell.y - 16}Z`} fill="currentColor" opacity="0.7" data-id="element-134" data-mp="33nydeb9vu3qm" />)}
      </g>
    </svg>;
}

/** Tall spike of tiny florets (heather / lavender) — decorative. */
export function FlowerSpike({
  className = '',
  petalClassName = '',
  centerClassName = ''
}: FlowerProps) {
  return <svg viewBox="0 0 60 200" fill="none" aria-hidden="true" className={className} data-id="element-135" data-mp="38mj6lqqpvn5k">
      <path d="M30 196V30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.6" className={centerClassName} data-id="element-136" data-mp="9yc4ti7xmsw0" />
      <g className={petalClassName} data-id="element-137" data-mp="547xgxur8iue">
        {Array.from({
        length: 11
      }, (_, index) => {
        const y = 28 + index * 11;
        const size = 4 + index * 0.35;
        return <g key={index} data-id="element-138" data-mp="3psxtcexx14xc">
              <circle cx={30 - size - 1} cy={y} r={size} fill="currentColor" opacity="0.75" data-id="element-139" data-mp="3bvizmy0mqazk" />
              <circle cx={30 + size + 1} cy={y + 5} r={size} fill="currentColor" opacity="0.6" data-id="element-140" data-mp="3guccqpc07zz7" />
            </g>;
      })}
        <circle cx="30" cy="20" r="4" fill="currentColor" opacity="0.8" data-id="element-141" data-mp="8e0wv4zpru60" />
      </g>
    </svg>;
}

/** Flower essence dropper bottle — decorative. */
export function DropperBottle({
  className = ''
}: DecorProps) {
  return <svg viewBox="0 0 80 160" fill="none" aria-hidden="true" className={className} data-id="element-142" data-mp="3ut2folli8tvh">
      <rect x="30" y="6" width="20" height="34" rx="5" fill="currentColor" opacity="0.75" data-id="element-143" data-mp="3n0yrweuz61x" />
      <rect x="24" y="36" width="32" height="14" rx="4" fill="currentColor" opacity="0.9" data-id="element-144" data-mp="rcv8i53vsehy" />
      <path d="M26 50h28c6 0 10 5 10 11v82c0 6-4 11-10 11H26c-6 0-10-5-10-11V61c0-6 4-11 10-11Z" fill="currentColor" opacity="0.32" data-id="element-145" data-mp="w3v8xkc7lep7" />
      <rect x="24" y="86" width="32" height="34" rx="4" fill="currentColor" opacity="0.22" data-id="element-146" data-mp="15hm3p1r1lyda" />
      <path d="M40 130c4-7 7-11 7-15a7 7 0 1 0-14 0c0 4 3 8 7 15Z" fill="currentColor" opacity="0.6" data-id="element-147" data-mp="11ga90htbamy8" />
    </svg>;
}